import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { animateMenu } from '../motion/controls';

const navigation = [
  { label: 'research', href: '/#modes', desktopId: '282', mobileId: '2bc' },
  { label: 'engineering', href: '/#quality', desktopId: '290', mobileId: '2ca' },
  { label: 'prototyping', href: '/#manufacturing', desktopId: '29e', mobileId: '2d8' },
  { label: 'contact', href: '/#contact', desktopId: '2ac', mobileId: '2e6' },
] as const;

interface NavigationLinksProps {
  mobile?: boolean;
  onNavigate?: () => void;
}

function NavigationLinks({ mobile = false, onNavigate }: NavigationLinksProps) {
  return navigation.map((item, index) => (
    <a
      key={item.href}
      href={item.href}
      className="nav_menu-element-wrapper w-inline-block"
      data-motion-id={`7e796c0e-c0bc-a749-dcbf-469fe5feb${mobile ? item.mobileId : item.desktopId}`}
      onClick={onNavigate}
    >
      <div className="nav_menu-element">
        <div className="nav_number" aria-hidden="true">
          <div>{index + 1}</div>
        </div>
        <div>{item.label}</div>
      </div>
      <div className="nav_menu-element_hover" aria-hidden="true">
        <div className="nav_menu-element_style-hover">
          <div className="nav_number">
            <div>{index + 1}</div>
          </div>
          <div>{item.label}</div>
        </div>
      </div>
    </a>
  ));
}

export function Header({ currentPath = '/' }: { currentPath?: string }) {
  const [open, setOpen] = useState(false);
  const header = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (!header.current) return;
    const animation = animateMenu(header.current, open);
    return () => {
      animation.kill();
    };
  }, [open]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 992px)');
    const closeOnDesktop = () => {
      if (desktop.matches) setOpen(false);
    };
    desktop.addEventListener('change', closeOnDesktop);
    return () => {
      desktop.removeEventListener('change', closeOnDesktop);
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      toggle.current?.focus();
    };
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !header.current?.contains(event.target)) setOpen(false);
    };

    document.addEventListener('keydown', closeOnEscape);
    document.addEventListener('pointerdown', closeOutside);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.removeEventListener('pointerdown', closeOutside);
    };
  }, [open]);

  return (
    <div ref={header} className="navbar_component" data-menu-open={open}>
      <nav className="navbar" aria-label="Main navigation">
        <div className="nav_wrapper">
          <div className="nav_container">
            <div id="w-node-_7e796c0e-c0bc-a749-dcbf-469fe5feb277-e5feb273" className="div-block-9">
              <a
                href="/"
                aria-current={currentPath === '/' ? 'page' : undefined}
                className={`brand w-nav-brand${currentPath === '/' ? ' is-active' : ''}`}
              >
                <img
                  src="/assets/images/Arcnova_Logo_Light.svg"
                  alt="Arcnova Logo"
                  className="nav_logo"
                />
              </a>
              <div className="nav_text">
                © 2026 Arcnova — Ylabs LLC. <br />
                All rights reserved
              </div>
            </div>
            <button
              ref={toggle}
              type="button"
              id="w-node-_7e796c0e-c0bc-a749-dcbf-469fe5feb27e-e5feb273"
              data-motion-id="7e796c0e-c0bc-a749-dcbf-469fe5feb27e"
              className="menu-button"
              aria-label={open ? 'Close navigation' : 'Open navigation'}
              aria-expanded={open}
              aria-controls="mobile-navigation"
              onClick={() => {
                setOpen((previous) => !previous);
              }}
            >
              <span className="menu_line top" aria-hidden="true" />
              <span className="menu_line bottom" aria-hidden="true" />
            </button>
            <div className="desktop_menu">
              <NavigationLinks />
            </div>
            <div
              id="w-node-_7e796c0e-c0bc-a749-dcbf-469fe5feb2ba-e5feb273"
              className="div-block-12"
            >
              <div id="mobile-navigation" className="mobile_menu" inert={!open}>
                <NavigationLinks
                  mobile
                  onNavigate={() => {
                    setOpen(false);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
}
